// How a job's page stays current. Status changes arrive as stream events (F3), so while the
// stream is live the page waits for them; while it is not (connecting, reconnecting, or the
// stream server is down), it reads the job again on a slow timer, so a page never sits stale.
// The outputs' signed URLs expire (G5), so the page also reads the job shortly before they do.
import type { StreamStatus } from "../../api/events";
import type { JobStatus } from "../../api/generated/model";
import { soonest, urlRefreshIn } from "../../api/signed-urls";
import { isSettled } from "./status";

export const FALLBACK_REFETCH_MS = 15_000;

/** A job that can still change: not loaded yet, or not settled. */
export function canChange(status: JobStatus | undefined): boolean {
  return status === undefined || !isSettled(status);
}

/** The fallback timer: off once the job has ended or while the stream is live. */
export function jobRefetchInterval(
  status: JobStatus | undefined,
  stream: StreamStatus | undefined,
): number | false {
  if (!canChange(status) || stream === "live") return false;
  return FALLBACK_REFETCH_MS;
}

/** The job page's timer: the fallback while the job can change, or fresh URLs, if sooner. */
export function jobPageInterval(
  job: { status: JobStatus; urls_expire_at: string | null } | undefined,
  stream: StreamStatus | undefined,
  now: number = Date.now(),
): number | false {
  return soonest(jobRefetchInterval(job?.status, stream), urlRefreshIn(job?.urls_expire_at, now));
}
